import * as R from 'ramda';
import { DateCount, TopHeader } from './competitive-metrics-constants';

const sum = (nums: number[]): number => nums.reduce((total, n) => total + n, 0);

export const uniqueByEquals = <T>(arr: T[]): T[] =>
  arr.reduce<T[]>(
    (acc, item) => (acc.some(existing => R.equals(existing, item)) ? acc : [...acc, item]),
    []
  );

export const sortByDate = <T extends { date: string }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => a.date.localeCompare(b.date));

// Darker green for higher values; a flat range (min === max) renders white.
export const getGreenShade = (value: number, min: number, max: number): string => {
  const pctVal = max === min ? 1 : Math.min(1, Math.max(0, 1 - (value - min) / (max - min)));
  const scaledRgbVal = Math.trunc(pctVal * 255);
  const scaledGreenVal = Math.trunc(pctVal * 100) + 155;

  const rgbHexVal = scaledRgbVal.toString(16).padStart(2, '0');
  const greenHexVal = scaledGreenVal.toString(16).padStart(2, '0');
  return `#${rgbHexVal}${greenHexVal}${rgbHexVal}`;
};

export const getCountsByWeek = (arr, keyName, countName): DateCount[] =>
  arr.map(obj => ({
    date: obj[keyName],
    count: sum(obj[countName].map(spotData => sum(spotData)))
  }));

export const filterOutInvalidDays = (
  countsByDate: DateCount[],
  validDays: string[]
): DateCount[] => {
  const filteredCountsByDate = countsByDate.filter(row => validDays.includes(row.date));
  const existingDates = filteredCountsByDate.map(row => row.date);
  for (const validDay of validDays) {
    if (!existingDates.includes(validDay)) {
      filteredCountsByDate.push({ date: validDay, count: 0 });
    }
  }
  return filteredCountsByDate;
};

export const getFirstPartOfHeaderName = (header: string): string => header.split('_')[0];

export const getRgbLimitsFromData = (data: number[][]) => {
  const rgbLimits = {
    details: {
      min: Infinity,
      max: -Infinity
    },
    networks: {
      min: Infinity,
      max: -Infinity
    },
    dayparts: {
      min: Infinity,
      max: -Infinity
    }
  };
  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = data[rowIndex];
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const value = data[rowIndex][colIndex];
      // If this is the total/total box, it doesn't count toward any min/max values
      if (colIndex === 0 && rowIndex === data.length - 1) {
        continue;
      } else if (colIndex === 0) {
        // If we're in the first column, we're getting the network min/max values
        rgbLimits.networks = {
          min: Math.min(rgbLimits.networks.min, value),
          max: Math.max(rgbLimits.networks.max, value)
        };
      } else if (rowIndex === data.length - 1) {
        // If we're in the last row, we're getting the daypart min/max values
        rgbLimits.dayparts = {
          min: Math.min(rgbLimits.dayparts.min, value),
          max: Math.max(rgbLimits.dayparts.max, value)
        };
      } else {
        // If we're not in the first row/column, we're getting min/max for the interior of the table.
        rgbLimits.details = {
          min: Math.min(rgbLimits.details.min, value),
          max: Math.max(rgbLimits.details.max, value)
        };
      }
    }
  }
  return rgbLimits;
};

export const getCsvHeaderLabel = (header: TopHeader): string => {
  if (typeof header === 'string') {
    return header;
  }
  const lengthLabel = header.length === undefined ? undefined : `${header.length}s`;
  return [header.network?.replace(/\n/, ''), lengthLabel].filter(Boolean).join(' ');
};
